import { useParams, Link } from "react-router-dom";
import SEO from "@/components/common/SEO";
import { pageMetadata } from "@/lib/seo";
import { articleSchema, toIsoDate } from "@/lib/structured-data";
import EnhancedFooter from "@/components/enhanced/EnhancedFooter";
import { useLanguage } from "@/contexts/LanguageContext";
import { getLocalizedRoute } from "@/lib/routes";
import { getInternalArticleSlug } from "@/lib/articleSlugMapping";
import { ArrowLeft } from "lucide-react";
import { getArticleBySlug, getRelatedArticles } from "@/data/articles";
import EnhancedNavbar from "@/components/enhanced/EnhancedNavbar";
import PageHero from "@/components/features/sections/PageHero";
import FitDisplay from "@/components/features/editorial/FitDisplay";
import PillButton from "@/components/ui/PillButton";
import Scramble from "@/components/motion/Scramble";
import { HERO_FIT_CLASS, heroFitStyle } from "@/lib/fitDisplay";

const ArticleDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const { language, t } = useLanguage();
  
  // The URL slug is language-specific (see src/lib/articleSlugMapping.ts), so
  // resolve it back to the article's internal slug before looking it up.
  const internalSlug = slug ? getInternalArticleSlug(slug) : undefined;
  const article = internalSlug ? getArticleBySlug(internalSlug) : undefined;
  const relatedArticles = article ? getRelatedArticles(article.slug, 3) : [];
  
  // Get translated content based on language
  const getTranslatedContent = () => {
    if (!article) return null;
    const isDutch = language === 'nl';
    return {
      title: isDutch && article.titleNl ? article.titleNl : article.title,
      // Shorter <title> for search results; falls back to the display headline.
      seoTitle: isDutch
        ? article.seoTitleNl || article.titleNl || article.seoTitle || article.title
        : article.seoTitle || article.title,
      excerpt: isDutch && article.excerptNl ? article.excerptNl : article.excerpt,
      content: isDutch && article.contentNl ? article.contentNl : article.content,
      category: isDutch && article.categoryNl ? article.categoryNl : article.category,
      depth: isDutch && article.depthNl ? article.depthNl : article.depth,
      tags: isDutch && article.tagsNl ? article.tagsNl : article.tags,
      readTime: isDutch && article.readTimeNl ? article.readTimeNl : article.readTime,
          publishedAt: isDutch && article.publishedAtNl ? article.publishedAtNl : article.publishedAt,
      metaDescription: isDutch && article.metaDescriptionNl ? article.metaDescriptionNl : (article.metaDescription || article.excerpt),
    };
  };
  
  const translated = getTranslatedContent();

  if (!article) {
    const notFound = t('articles.detail.articleNotFound');
    return (
      <div className={`min-h-screen bg-ink ${HERO_FIT_CLASS}`} style={heroFitStyle([notFound])}>
        <SEO
          title={t('articles.detail.articleNotFound')}
          description={t('articles.detail.articleNotFoundDescription')}
          noindex={true}
        />
        <EnhancedNavbar />
        <main>
          <PageHero label={t('nav.articles')} lead={notFound} intro={t('articles.detail.articleNotFoundDescription')}>
            <PillButton to={getLocalizedRoute("/articles", language)} tone="light">
              {t('articles.detail.backToArticles')}
            </PillButton>
          </PageHero>
        </main>
        <EnhancedFooter />
      </div>
    );
  }

  if (!translated) return null;

  // Canonical path of this article in the current language.
  const articlePath = getLocalizedRoute(`/articles/${article.slug}`, language);

  // Turn markdown links into anchors. Links to other articles are rebuilt for the
  // current language, so a content slug in the wrong language can't produce a
  // URL that isn't served (e.g. a Dutch slug under the English /articles/ path).
  const renderLinks = (html: string) =>
    html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_match, text, href) => {
      const articleLink = href.match(/^\/(?:nl\/)?articles\/([^/]+)$/);
      if (articleLink) {
        const localizedHref = getLocalizedRoute(`/articles/${getInternalArticleSlug(articleLink[1])}`, language);
        return `<a href="${localizedHref}" class="text-black underline hover:text-gray-600">${text}</a>`;
      }
      return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="text-black underline hover:text-gray-600">${text}</a>`;
    });

  return (
    <div className="min-h-screen bg-paper text-ink">
      <SEO
        {...pageMetadata({
          title: `${translated.seoTitle} | Byteet`,
          description: translated.metaDescription,
          image: article.image,
          type: "article",
          breadcrumbs: [
            { name: "Home", path: getLocalizedRoute("/", language) },
            { name: language === 'nl' ? "Artikelen" : "Articles", path: getLocalizedRoute("/articles", language) },
            { name: translated.title, path: articlePath },
          ],
          jsonLd: {
            ...articleSchema({
              title: translated.title,
              description: translated.metaDescription,
              path: articlePath,
              datePublished: toIsoDate(article.publishedAt),
              image: article.image,
            }),
            articleSection: translated.category,
            keywords: (translated.tags || []).join(', '),
            wordCount: Math.round((translated.content || '').split(/\s+/).length),
            inLanguage: language === 'nl' ? 'nl' : 'en',
          },
        })}
      />
      <EnhancedNavbar />

      <main>
        {/* Header: an ink band like PageHero's, but with the title in a
            smaller display size, since article titles run long. */}
        <section className="bg-ink px-5 pb-14 pt-32 text-white md:px-8 md:pb-20 md:pt-44">
          <Link
            to={getLocalizedRoute("/articles", language)}
            className="label-mono inline-flex items-center gap-2 text-white/60 transition-colors hover:text-white"
          >
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
            {t('articles.detail.backToArticles')}
          </Link>
          <p className="label-mono mb-6 mt-12 text-white/55 md:mt-16">
            <Scramble text={[translated.category, translated.depth].filter(Boolean).join(" / ")} />
          </p>
          <div className="max-w-6xl">
            <FitDisplay as="h1" lines={[translated.title]} size="clamp(2.2rem,5.2vw,5.5rem)" className="leading-[0.95]" />
          </div>
          <div className="mt-10 grid gap-6 border-t border-white/20 pt-8 md:mt-14 md:grid-cols-12">
            <p className="label-mono flex flex-wrap gap-x-6 gap-y-1 text-white/55 md:col-span-3 md:flex-col">
              <span>{translated.publishedAt}</span>
              <span>{translated.readTime}</span>
            </p>
            <p className="max-w-2xl text-lg leading-relaxed text-white/75 md:col-span-9 md:text-xl">{translated.excerpt}</p>
          </div>
        </section>

        {/* Article image, in greyscale like the rest of the site. */}
        <div className="px-5 pt-10 md:px-8 md:pt-16">
          <div className="relative mx-auto aspect-[2/1] max-w-5xl overflow-hidden rounded-sm bg-ink/10">
            <img
              src={article.image}
              alt={translated.title}
              width={1200}
              height={600}
              className="h-full w-full object-cover grayscale"
              loading="eager"
              fetchPriority="high"
              decoding="async"
              onError={(e) => {
                // Fallback to placeholder if image fails to load
                const target = e.target as HTMLImageElement;
                target.src = `https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1200&h=600&fit=crop&q=80&auto=format&webp`;
              }}
            />
          </div>
        </div>

        {/* Article Content */}
        <section className="px-5 pb-16 pt-12 md:px-8 md:pb-24 md:pt-20">
          <div className="mx-auto max-w-[44rem]">
            <div className="article-prose">
              {translated.content.split('\n\n').map((paragraph, idx) => {
                // Handle headers
                if (paragraph.match(/^#{1,6}\s/)) {
                  const level = paragraph.match(/^#+/)?.[0].length || 1;
                  // The article title above is the page's only <h1>. A leading "# Title"
                  // in the content repeats it, so drop it; other headings start at <h2>.
                  if (level === 1 && idx === 0) return null;
                  const text = paragraph.replace(/^#+\s/, '').trim();
                  const HeadingTag = `h${Math.max(2, level)}` as keyof JSX.IntrinsicElements;
                  const headingClasses = level <= 2 ? 'text-[1.75rem] md:text-[2.1rem]' : 'text-xl md:text-2xl';
                  return <HeadingTag key={idx} className={`break-words font-semibold leading-[1.12] tracking-[-0.025em] text-ink ${headingClasses} mb-5 mt-14 first:mt-0`}>{text}</HeadingTag>;
                }
                // Handle code blocks
                if (paragraph.trim().startsWith('```')) {
                  const codeContent = paragraph.replace(/```[\w]*\n?/g, '').trim();
                  return (
                    <pre key={idx} className="my-8 overflow-x-auto rounded-sm bg-ink p-5 text-paper">
                      <code className="font-mono text-sm">{codeContent}</code>
                    </pre>
                  );
                }
                // Handle lists
                if (paragraph.trim().startsWith('- ') || paragraph.trim().startsWith('* ') || paragraph.match(/^\d+\.\s/)) {
                  const items = paragraph.split('\n').filter(line => line.trim());
                  return (
                    <ul key={idx} className="my-6 list-disc space-y-2.5 pl-5 text-lg text-ink/80 marker:text-ink/40">
                      {items.map((item, itemIdx) => {
                        const text = item.replace(/^[-*\d+\.]\s/, '').trim();
                        // Process bold and code in list items
                        const processedText = text
                          .replace(/\*\*(.+?)\*\*/g, '<strong class="font-bold text-black">$1</strong>')
                          .replace(/`(.+?)`/g, '<code class="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono">$1</code>');
                        return (
                          <li key={itemIdx} className="pl-1 leading-[1.65]" dangerouslySetInnerHTML={{ __html: processedText }} />
                        );
                      })}
                    </ul>
                  );
                }
                // Handle tables (GitHub-flavored markdown)
                const tableRows = paragraph.trim().split('\n').map((l) => l.trim());
                if (
                  tableRows.length >= 2 &&
                  tableRows[0].startsWith('|') &&
                  /^\|?[\s:|-]+\|?$/.test(tableRows[1]) &&
                  tableRows[1].includes('-')
                ) {
                  const parseRow = (line: string) =>
                    line.replace(/^\s*\|/, '').replace(/\|\s*$/, '').split('|').map((c) => c.trim());
                  const processCell = (text: string) =>
                    renderLinks(
                      text
                        .replace(/\*\*(.+?)\*\*/g, '<strong class="font-bold text-black">$1</strong>')
                        .replace(/`(.+?)`/g, '<code class="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono">$1</code>')
                    );
                  const headers = parseRow(tableRows[0]);
                  const bodyRows = tableRows.slice(2).filter((l) => l.startsWith('|')).map(parseRow);
                  return (
                    <div key={idx} className="my-8 overflow-x-auto">
                      <table className="w-full border-collapse text-left text-base">
                        <thead>
                          <tr>
                            {headers.map((header, hIdx) => (
                              <th
                                key={hIdx}
                                className="border-b border-ink/30 px-3 py-3 align-bottom font-semibold text-ink first:pl-0"
                                dangerouslySetInnerHTML={{ __html: processCell(header) }}
                              />
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {bodyRows.map((row, rIdx) => (
                            <tr key={rIdx}>
                              {row.map((cell, cIdx) => (
                                <td
                                  key={cIdx}
                                  className="border-b border-ink/15 px-3 py-3 align-top text-ink/80 first:pl-0"
                                  dangerouslySetInnerHTML={{ __html: processCell(cell) }}
                                />
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                }
                // Regular paragraphs
                if (paragraph.trim()) {
                  // Process bold, code, and markdown links
                  const processedParagraph = renderLinks(
                    paragraph
                      .replace(/\*\*(.+?)\*\*/g, '<strong class="font-bold text-black">$1</strong>')
                      .replace(/`(.+?)`/g, '<code class="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono">$1</code>')
                  );
                  
                  return (
                    <p 
                      key={idx} 
                      className="mb-6 text-lg leading-[1.7] text-ink/80" 
                      dangerouslySetInnerHTML={{ __html: processedParagraph }} 
                    />
                  );
                }
                return null;
              })}
            </div>

            {translated.tags.length > 0 && (
              <ul className="mt-14 flex flex-wrap gap-x-5 gap-y-2 border-t border-ink/15 pt-6">
                {translated.tags.map((tag, idx) => (
                  <li key={`${tag}-${idx}`} className="label-mono text-ink/50">
                    {tag}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        {/* Related articles, as ruled rows. */}
        {relatedArticles.length > 0 && (
          <section className="px-5 pb-24 md:px-8 md:pb-36">
            <header className="mb-8 border-t border-ink/15 pt-10 md:mb-12 md:pt-14">
              <h2 className="label-mono text-ink/50">
                <Scramble text={t('articles.detail.relatedArticles')} />
              </h2>
            </header>
            <ul className="border-b border-ink/15">
              {relatedArticles.map((related) => {
                const isDutch = language === 'nl';
                return (
                  <li key={related.id}>
                    <Link
                      to={getLocalizedRoute(`/articles/${related.slug}`, language)}
                      className="group grid gap-y-3 border-t border-ink/15 py-7 transition-colors duration-300 hover:bg-ink hover:text-paper md:grid-cols-12 md:gap-x-8 md:px-2 md:py-9"
                    >
                      <p className="label-mono text-ink/50 transition-colors group-hover:text-paper/60 md:col-span-3">
                        {isDutch && related.categoryNl ? related.categoryNl : related.category}
                      </p>
                      <div className="min-w-0 md:col-span-8">
                        <h3 className="break-words text-[clamp(1.4rem,2.4vw,2.2rem)] font-semibold leading-[1.08] tracking-[-0.03em]">
                          {isDutch && related.titleNl ? related.titleNl : related.title}
                        </h3>
                        <p className="mt-3 line-clamp-2 max-w-2xl text-ink/60 transition-colors group-hover:text-paper/70">
                          {isDutch && related.excerptNl ? related.excerptNl : related.excerpt}
                        </p>
                      </div>
                      <span className="flex items-start md:col-span-1 md:justify-end">
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </section>
        )}
      </main>

      <EnhancedFooter />
    </div>
  );
};

export default ArticleDetailPage;
